import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import './TopNavD1.scss';

const getFreeShippingThreshold = (envios) => {
  if (!Array.isArray(envios) || envios.length === 0) return null;
  const mins = envios
    .map((e) => Number(e?.monto_minimo || 0))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (mins.length === 0) return null;
  return Math.min(...mins);
};

const formatMoney = (value) => `$${Number(value || 0).toLocaleString('es-AR')}`;

const splitMessages = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean);
  }
  return String(value)
    .split(/\s*(?:\||\u2022|;|\n)\s*/g)
    .map((item) => item.trim())
    .filter(Boolean);
};

const getClientMessages = (client) => {
  const listFields = [
    client?.topbar_messages,
    client?.topbarMessages,
    client?.top_messages,
    client?.topMessages,
  ];

  const listMessages = listFields
    .map((field) => splitMessages(field))
    .find((messages) => messages.length > 0);
  if (listMessages) return listMessages;

  return [
    client?.topbar_phrase,
    client?.topbarPhrase,
    client?.top_message_left,
    client?.topMessageLeft,
    client?.top_message_center,
    client?.topMessageCenter,
    client?.top_message_right,
    client?.topMessageRight,
  ].map((item) => String(item || '').trim()).filter(Boolean);
};

const getShippingNames = (envios) => {
  if (!Array.isArray(envios)) return [];
  return envios
    .map((envio) => String(envio?.name || '').trim())
    .filter(Boolean);
};

const getLowestShippingCost = (envios) => {
  if (!Array.isArray(envios) || envios.length === 0) return null;
  const costs = envios
    .map((envio) => Number(envio?.monto || 0))
    .filter((value) => Number.isFinite(value) && value > 0);
  return costs.length ? Math.min(...costs) : null;
};

const hasCalculatedShipping = (envios) => (
  Array.isArray(envios) && envios.some((envio) => Boolean(envio?.calculated_by_system))
);

const marqueeRepeaters = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

const getInstallmentsSummary = (cuotasMP, showMPButton) => {
  if (showMPButton === false || !Array.isArray(cuotasMP)) return null;

  let maxZeroInterest = 0;
  let hasInstallments = false;

  cuotasMP.forEach((brandData) => {
    (brandData?.rates || []).forEach((rateGroup) => {
      (rateGroup?.payer_costs || []).forEach((rateOption) => {
        const installments = Number(rateOption?.installments || 0);
        const rate = Number(rateOption?.rate ?? rateOption?.installment_rate ?? 0);
        if (installments > 1) hasInstallments = true;
        if (rate === 0 && installments > maxZeroInterest) maxZeroInterest = installments;
      });
    });
  });

  return { maxZeroInterest, hasInstallments };
};

const TopNavD1 = ({
  client, envios, minimumPurchase, cuotasMP, showMPButton,
}) => {
  const messages = useMemo(() => {
    const items = getClientMessages(client);

    const shippingNames = getShippingNames(envios);
    if (shippingNames.length > 0) {
      const visibleNames = shippingNames.slice(0, 3).join(', ');
      const hiddenCount = shippingNames.length - 3;
      items.push(`Envíos: ${visibleNames}${hiddenCount > 0 ? ` +${hiddenCount}` : ''}`);
    }

    const minPurchase = Number(minimumPurchase || 0);
    if (Number.isFinite(minPurchase) && minPurchase > 0) {
      items.push(`Compra mínima: ${formatMoney(minPurchase)}`);
    }

    const freeFrom = getFreeShippingThreshold(envios);
    if (freeFrom) {
      items.push(`Envío gratis desde ${formatMoney(freeFrom)}`);
    }

    const lowestShippingCost = getLowestShippingCost(envios);
    if (lowestShippingCost) {
      items.push(`Envíos desde ${formatMoney(lowestShippingCost)}`);
    } else if (hasCalculatedShipping(envios)) {
      items.push('Costo de envío calculado al finalizar');
    }

    const installments = getInstallmentsSummary(cuotasMP, showMPButton);
    if (installments?.maxZeroInterest >= 2) {
      items.push(`Hasta ${installments.maxZeroInterest} cuotas sin interés`);
    } else if (installments?.hasInstallments) {
      items.push('Cuotas disponibles con Mercado Pago');
    }

    return items.filter(Boolean);
  }, [client, cuotasMP, envios, minimumPurchase, showMPButton]);

  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(!!mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else if (mq.addListener) mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else if (mq.removeListener) mq.removeListener(update);
    };
  }, []);

  if (!messages || messages.length === 0) return null;

  const repeatedMessage = messages.join(' - ');

  return (
    <div
      className={`topnav-d1 topnav-d1--marquee${reduceMotion ? ' topnav-d1--reduce-motion' : ''}`}
      data-testid="topnav-d1"
    >
      <div className="topnav-d1__marquee" aria-label="Avisos">
        {marqueeRepeaters.map((suffix) => (
          <span key={`topnav-marquee-${suffix}`}>{repeatedMessage}</span>
        ))}
      </div>
    </div>
  );
};

TopNavD1.defaultProps = {
  client: null,
  envios: null,
  minimumPurchase: 0,
  cuotasMP: null,
  showMPButton: false,
};

const messageListPropType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
]);

TopNavD1.propTypes = {
  client: PropTypes.shape({
    topbar_messages: messageListPropType,
    topbarMessages: messageListPropType,
    top_messages: messageListPropType,
    topMessages: messageListPropType,
    topbar_phrase: PropTypes.string,
    topbarPhrase: PropTypes.string,
    top_message_left: PropTypes.string,
    topMessageLeft: PropTypes.string,
    top_message_center: PropTypes.string,
    topMessageCenter: PropTypes.string,
    top_message_right: PropTypes.string,
    topMessageRight: PropTypes.string,
  }),
  envios: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    monto: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    monto_minimo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    calculated_by_system: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  })),
  minimumPurchase: PropTypes.number,
  cuotasMP: PropTypes.arrayOf(PropTypes.shape()),
  showMPButton: PropTypes.bool,
};

export default TopNavD1;






